import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationListPageActions, ApplicationNotInReviewStatus } from "./model";
import { sendGetUserAccessDataEmail } from "../applicationDetail/service";
import { errorMsg, successMsg } from "../../../components/simpleMessageAlert";
import { AlertMessage } from "../../../common/data/AlertMsg";
import { isNullOrUndefined } from "../../../common/utils";
import { RootState } from "../../../redux/store";
import { AppSideBarPageActions, SideBarOptions } from "../../../parts/AppSideBar/model";
import { IsFeatureEnabledOrNot } from "../../../FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "../../../FeatureToggles/toggleList";

export const ApplicationListController = () => {
  const dispatch = useDispatch();
  const isReviewCycleEnabled = IsFeatureEnabledOrNot(ToggleList.ReviewCycle);
  const {
    value: { data, currentReviewCycleStartDate },
    progress,
  } = useSelector((rootState: RootState) => rootState.applicationList.applicationList);
  const appStatusesWhenTaskCountWillBeDisplayed = [
    "Review in Progress",
    "Review Closed, Waiting for Upload",
    "Review Verification in Progress",
    "Review Completed and Verified",
  ];
  const isTaskAssignedToUser = (review) => review?.noOfTasksAssignedToUser?.total > 0;

  const isMyTasksColumnVisible = !!data && data.some((it: any) => {
    return isTaskAssignedToUser(it.recentReviews?.inProgressReview)
      || (isTaskAssignedToUser(it.recentReviews?.lastClosedReview)
        && appStatusesWhenTaskCountWillBeDisplayed.includes(it.currentStatus)
      );
  });

  useEffect(() => {
    dispatch(ApplicationListPageActions.getApplications());
    dispatch(AppSideBarPageActions.updateSelectedSideBar(SideBarOptions.APPLICATIONS));
  }, []);

  const dataSource = data?.map((it) => {
    let reviewStatus = isReviewCycleEnabled ? it.currentStatus : it.recentReviews?.inProgressReview?.status;
    const isReviewable = it.isReviewable;
    reviewStatus = isNullOrUndefined(reviewStatus) ? ApplicationNotInReviewStatus : reviewStatus;
    reviewStatus = isReviewable === false ? "Not Reviewable" : reviewStatus;
    const globalAccessApproverDisplay: string[] = [];
    it.globalAccessApprovers.forEach((gaa) => {
      globalAccessApproverDisplay.push(`${gaa.name} (${gaa.jobProfile})`);
    });
    const data = {
      id: it.id,
      name: it.name,
      businessFunctions: it.businessFunctions,
      globalAccessApprovers: it.globalAccessApprovers.map((gaa) => ({
        name: `${gaa.name} (${gaa.jobProfile})`,
        employeeId: gaa.employeeId,
      })),
      totalReviewee: it.totalReviewee,
      status: reviewStatus,
      currentStatus: it.currentStatus,
      lastReviewedOn: it.recentReviews?.lastClosedReview?.endDate,
      lastImportedOn: it.lastImportedOn === "" ? undefined : it.lastImportedOn,
      accessTypeLink: it.accessTypeLink,
      gitsProductTeamEmail: it.gitsProductTeam.email,
      lastReviewName: it.recentReviews?.lastClosedReview?.name,
      lastReviewId: it.recentReviews?.lastClosedReview?.id,
      currentActiveReviewName: it.recentReviews?.inProgressReview?.name,
      currentActiveReviewId: it.recentReviews?.inProgressReview?.id,
      isReviewable: isReviewable,
      complianceTags: it.complianceTags,
      globalAccessApproverDisplay,
      uniqueName: it.uniqueName,
      hasUploadPermission: it.hasUploadPermission,
      recentReview: it.recentReviews?.inProgressReview || it.recentReviews?.lastClosedReview,
      applicableActions: [],
    };
    if (isReviewCycleEnabled)
      data.applicableActions = it.applicableActions;
    return data;
  });

  async function sendGetLatestUserAccessDataEmail(appId, applicationGitsProductTeamEmail) {
    try {
      await sendGetUserAccessDataEmail(appId);
      successMsg(
        AlertMessage.requestUserAccessData.successMessage(applicationGitsProductTeamEmail),
      );
    } catch {
      errorMsg(AlertMessage.requestUserAccessData.errorMessage);
    }
  }

  return {
    dataSource: dataSource,
    isLoading: !!progress,
    isMyTasksColumnVisible,
    sendGetLatestUserAccessDataEmail: sendGetLatestUserAccessDataEmail,
    currentReviewCycleStartDate,
    canDisplayTasksCount: (app) => {
      return appStatusesWhenTaskCountWillBeDisplayed.includes(app.currentStatus)
        && app.recentReview
        && isTaskAssignedToUser(app.recentReview);
    },
  };
};
